'use strict';

import React from 'react';
import PropTypes from 'prop-types';
const map = require('lodash/map');
const cloneDeep = require('lodash/cloneDeep');
const filter = require('lodash/filter');
const has = require('lodash/has');
const keys = require('lodash/keys');
const moment = require('moment-timezone');

const Globals = require('../../../../app/globals');
const StringUtil = require('../../../../app/shared/StringUtil');
const TimeUtil = require('../../../../app/shared/TimeUtil');
const Strings = require('../../../../app/shared/Strings');
const SortUtil = require('../../util/SortUtil');
const KeyboardUtil = require('../../util/keyboard');
import TableHeaderSortIcon from './TableHeaderSortIcon';

// Use this general format for props.columns
// let columns = [
//         {
//             displayName: 'ID',
//             key:'id',
//             width: '33%', (optional)
//             type: 'int'
//         },
//         {
//             displayName: 'Name',
//             key:'name',
//             width: '200px', (optional)
//             type: 'string'
//         },
//         {
//             displayName: 'Activity ID',
//             key:'activity_id',
//             type: 'int'
//         },
//         {
//             displayName: 'Create Date',
//             key:'create_date',
//             type: 'date'
//         },
//         {
//             displayName: 'Time Played',
//             key:'time_played',
//             type: 'duration'
//         }
//         {
//             displayName: 'Admin',
//             key:'is_admin',
//             type: 'bool'
//         }
//     ];
//TODO: specifiy columns as part of props. add keys: searchable, sortable

// props.topGroupHeader format:
// topGroupHeader = [
//     {
//         displayName: 'Some group',
//         colSpan: '4',
//         width: '33%', (optional)
//     },
//     {
//         displayName: 'Another group',
//         colspan: '2'
//     }
// ];

/*
props.displayColumnCallbacks format:
displayColumnCallbacks = {
    someStringKey: function(rawColumnData, uniqueRowKey) { return rawColumnData.toUpperCase(); },
    someIntKey: function(rawColumnData, uniqueRowKey) { return Math.round(rawColumnData); },
    etc...
}
*/

/*
props.displayColumnCallbacksId format:
displayColumnCallbacksId = {
    someKey: function(uniqueRowKey) { return Math.round(dataFromParentContext[uniqueRowKey].someFloatingPoint); },
    etc...
}
*/

/*
props.displayColumnCallbacksRow format:
displayColumnCallbacksRow = {
    someKey: function(rawRowData, uniqueRowKey) { return <div> { rawRowData['someKey'] + rawRowData['someOtherKey'] } </div>; }); },
    etc...
}
*/

const propTypes = {
    className: PropTypes.string,
    classNameTable: PropTypes.string,
    classNameTableContainer: PropTypes.string,
    title: PropTypes.string,
    rows: PropTypes.array,
    columns: PropTypes.arrayOf(PropTypes.shape({
        displayName: PropTypes.string,
        key: PropTypes.string.isRequired,
        size: PropTypes.number,
        width: PropTypes.string,
        type: PropTypes.string.isRequired,
        sortable: PropTypes.bool,
        generateId: PropTypes.bool,
    })),
    topGroupHeader: PropTypes.arrayOf(PropTypes.shape({
        displayName: PropTypes.string,
        colSpan: PropTypes.string,
        width: PropTypes.string,
    })),
    uniqueRowKey: PropTypes.string,
    onRowClick: PropTypes.func,
    selectedRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateFormat: PropTypes.string,
    dateTimezone: PropTypes.string,
    dateFormatUTC: PropTypes.bool,
    showTotal: PropTypes.bool,
    defaultSort: SortUtil.SortObjPropType.isRequired,
    hideSearch: PropTypes.bool,
    displayColumnCallbacks: PropTypes.object,
    displayColumnCallbacksId: PropTypes.object,
    displayColumnCallbacksRow: PropTypes.object,
    rowsPerPage: PropTypes.number, // if specified, page controls are shown
    rowExtraElements: PropTypes.func,
    rowClassName: PropTypes.func,
    alwaysRenderExtraRowContent: PropTypes.bool, // always render a extraRow which includes id attr. supports aria-controls for expansion buttons
};

const defaultProps = {
    className: '',
    classNameTable: '',
    classNameTableContainer: '',
    rows: [],
    columns: [],
    title: "",
    uniqueRowKey: "id",
    onRowClick: null,
    selectedRowKey: null,
    dateFormat: null,
    dateTimezone: '',
    dateFormatUTC: false,
    showTotal: true,
    defaultSort: {
        key: 'id',
        type: 'int',
        asc: true
    },
    topGroupHeader: [],
    hideSearch: false,
    displayColumnCallbacks: {},
    displayColumnCallbacksId: {},
    displayColumnCallbacksRow: {},
    rowsPerPage: 0,
    rowExtraElements: null,
    rowClassName: null,
    alwaysRenderExtraRowContent: false,
};

class SortableTable2 extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            sort: props.defaultSort,
            currentPage: 1,
        };

        this.rowFormatter = this.rowFormatter.bind(this);
        this.rowClassNameGetter = this.rowClassNameGetter.bind(this);
        this.onFilterChange = this.onFilterChange.bind(this);
        this.onFilterKeyDown = this.onFilterKeyDown.bind(this);
        this.onApplyFilterChange = this.onApplyFilterChange.bind(this);
        this.sort = this.sort.bind(this);
        this._filterRows = this._filterRows.bind(this);
        this.setPage = this.setPage.bind(this);        
        this.onRowClick = this.onRowClick.bind(this);
        this.renderIconButton = this.renderIconButton.bind(this);
        this.onMouseEnterRow = this.onMouseEnterRow.bind(this);
        this.onMouseLeaveRow = this.onMouseLeaveRow.bind(this);
    }

    componentWillUnmount() {
        if(this.props.onRowClick) { // only change cursor if selection is possible
            document.body.style.cursor = 'auto';
        }
    }
    
    onMouseEnterRow(e, index, user) {
        if(this.props.onRowClick) { // only change cursor if selection is possible
            document.body.style.cursor = 'pointer';
        }
    }

    onMouseLeaveRow(e, index, user) {
        if(this.props.onRowClick) { // only change cursor if selection is possible
            document.body.style.cursor = 'auto';
        }
    }

    rowFormatter(row) {
        const self = this;
        const clonedRow = cloneDeep(row);
        let formattedRow = {};
        Object.keys(clonedRow).forEach((columnKey) => { formattedRow[columnKey] = { display_elem: clonedRow[columnKey], text: clonedRow[columnKey] }});

        if (this.props.dateFormat) {
            const dateKeys = filter(this.props.columns, {type: 'date'}).map(column => { return column.key });
            dateKeys.forEach(dateKey => {
                if (!!formattedRow[dateKey]) {
                    if (!!formattedRow[dateKey].display_elem) {
                        let dateFormatted;
                        const date = formattedRow[dateKey].display_elem;
                        if (self.props.dateTimezone) {
                            dateFormatted = TimeUtil.convertToTimeZone(date, self.props.dateTimezone).format(self.props.dateFormat)
                        }
                        else if (!self.props.dateFormatUTC) {
                            dateFormatted = moment.tz(date, moment.tz.guess()).format(self.props.dateFormat);
                        }
                        else {
                            dateFormatted = moment(date).utc().format(self.props.dateFormat);
                        }

                        formattedRow[dateKey] = { display_elem: dateFormatted, text: dateFormatted };
                    }
                    else {
                        formattedRow[dateKey] = { display_elem: '', text: Strings['en'].date.NOT_AVAILABLE };
                    }
                }
            });
        }

        const durationKeys = filter(this.props.columns, { type: 'duration' }).map(column => column.key);
        durationKeys.forEach((durationKey) => {
            if (has(formattedRow, durationKey)) {
                const dur = formattedRow[durationKey].display_elem || 0;
                const formattedDuration = StringUtil.getTimeStringForMs(dur);
                formattedRow[durationKey] = { display_elem: formattedDuration, text: formattedDuration };
            }
        });

        const boolKeys = filter(this.props.columns, { type: 'bool' }).map(column => column.key);
        boolKeys.forEach((boolKey) => {
            const formattedBool = !!formattedRow[boolKey].display_elem ? 'True' : '';
            formattedRow[boolKey] = { display_elem: formattedBool, text: formattedBool };
        });

        keys(this.props.displayColumnCallbacks).forEach((colKey) => {
            let display_elem = self.props.displayColumnCallbacks[colKey](row[colKey], row[self.props.uniqueRowKey]);
            let text = null;
            if (typeof display_elem === 'string') {
                text = display_elem;
            }
            formattedRow[colKey] = {
                display_elem,
                text,
            };
        });

        keys(this.props.displayColumnCallbacksId).forEach((colKey) => {
            let display_elem = self.props.displayColumnCallbacksId[colKey](row[self.props.uniqueRowKey]);
            let text = null;
            if (typeof display_elem === 'string') {
                text = display_elem;
            }
            formattedRow[colKey] = {
                display_elem,
                text,
            };
        });

        keys(this.props.displayColumnCallbacksRow).forEach((colKey) => {
            let display_elem = self.props.displayColumnCallbacksRow[colKey](row, row[self.props.uniqueRowKey]);
            let text = null;
            if (typeof display_elem === 'string') {
                text = display_elem;
            }
            formattedRow[colKey] = {
                display_elem,
                text,
            };
        });

        return formattedRow;
    }

    rowClassNameGetter(row) {
        if(!!this.props.selectedRowKey && row[this.props.uniqueRowKey] === this.props.selectedRowKey) {
            return 'row-highlighted';
        }

        return '';
    }

    onFilterChange(e) {
        let unfilteredPages = 1;
        if (this.props.rowsPerPage) {
            unfilteredPages = Math.ceil(this.props.rows.length / this.props.rowsPerPage);     
        }            
        if (unfilteredPages < 2) {
            this.setState({ filter: e.target.value });
        }
        // else we show a magnify icon or wait for return before applying changes
    }
    
    onFilterKeyDown(e) {
        if(KeyboardUtil.isEnterKey(e.keyCode)){
            this.onApplyFilterChange();
        }        
    }
    
    onApplyFilterChange() {
        var elem = this.refs.search_input;
        this.setState({ filter: elem.value });
    }    

    sort(column, e) {
        e.preventDefault();
        const newSortKey = column.sortKey || column.key; // use sortKey if specified, otherwise use key
        const newSortObj = SortUtil.updateSortObj(this.state.sort, newSortKey, column.type);
        this.setState({ sort: newSortObj });
    }

    _filterRows() {
        const _rows = this.props.rows;

        if (!this.state.filter) {
          return _rows;
        }

        const filterBy = this.state.filter.toLowerCase();
        const size = _rows.length; //the benefit of precalculating this is debateable, todo: test
        const columnKeysSize = this.props.columns.length;

        let filteredRows = [];
        for (let rowIndex = 0; rowIndex < size; rowIndex++) {

            let matched = false;
            for(let columnIndex = 0; columnIndex < columnKeysSize; columnIndex++) {
                if(matched) continue; //optimization

                const columnKey = this.props.columns[columnIndex].key;

                if(!_rows[rowIndex][columnKey]) continue; //ignore falsy columns

                if(_rows[rowIndex][columnKey].toString().toLowerCase().indexOf(filterBy) !== -1) {
                    filteredRows.push(_rows[rowIndex]);
                    matched = true;
                }
            }
        }

        return filteredRows;
    }
    
    setPage(page) {
        this.setState({currentPage: page});
    }

    onRowClick(uniqueRowKey, e) {
        if (!!this.props.onRowClick) {
            this.props.onRowClick(uniqueRowKey);
        }
    }

    renderIconButton(cellData,cellDataKey,rowData,rowIndex,columnData,width) {
        return (<i className={cellData.className} onClick={
            function(e) {
                e.stopPropagation();
                cellData.onClick();
            }
        }/>)
    }

    render() {
        const self = this;
        let rows = this._filterRows();
        const filtered_length=rows.length; // before paging
        rows = SortUtil.sortObjectArray(rows, this.state.sort);

        let pageControls;
        if (this.props.rowsPerPage) {
            let numPages = Math.ceil(rows.length / this.props.rowsPerPage);            
            if (numPages>1) {
                let currentPage = this.state.currentPage;
                rows = rows.slice((currentPage-1)*this.props.rowsPerPage,currentPage*this.props.rowsPerPage);
                
                // show 10 page numbers at a time
                var pageButtons = [];
                var firstPage = Math.max(1,currentPage - 4);
                var lastPage = Math.min(firstPage + 9,numPages);
                var pageSet = Math.ceil(currentPage/10);
                var prevSetPage = (pageSet - 1) * 10-5;
                var nextSetPage = (pageSet + 1) * 10-5;
                
                if (firstPage > 1) {
                    pageButtons.push(
                        <button key={1} className="btn btn-primary" onClick={this.setPage.bind(this,1)}>
                            {'|<'}
                        </button>
                    );
                }
                
                if (prevSetPage > 1) {
                    pageButtons.push(
                        <button key={prevSetPage} className="btn btn-primary" onClick={this.setPage.bind(this,prevSetPage)}>
                            {'<<'}
                        </button>
                    );
                }
                
                for (var page=firstPage; page <=lastPage; ++page) {
                    if (page === currentPage) {
                        pageButtons.push(
                            <span key={page} className="current-page">{page}</span>
                        );
                    }
                    else {
                        var pageFunc = this.setPage.bind(this,page);
                        pageButtons.push(
                            <button key={page} className="btn btn-primary" onClick={pageFunc}>
                                {page}
                            </button>
                        );
                    }
                }
                
                if (nextSetPage < numPages) {
                    pageButtons.push(
                        <button key={nextSetPage} className="btn btn-primary" onClick={this.setPage.bind(this,nextSetPage)}>
                            {'>>'}
                        </button>
                    );
                }
                
                if (lastPage < numPages) {
                    pageButtons.push(
                        <button key={numPages} className="btn btn-primary" onClick={this.setPage.bind(this,numPages)}>
                            {'>|'}
                        </button>
                    );
                }
                pageControls = (
                    <div className='student-page-controls'>
                        {pageButtons}
                    </div>
                );
            }
        } // if rowsPerPage specified
        
        function getColumnStyle(column) {
            let style = {};

            if (column.width) {
                style = { ...style, width: column.width };
            }

            return style;
        }

        function renderHeaderCol(column) {
            if (typeof column.sortable === 'boolean' && !column.sortable) {
                return (
                    <th
                        key={column.key}
                        title={column.displayName}
                        style={getColumnStyle(column)}
                        role="columnheader"
                        scope="col"
                    >
                        <span>{column.displayName}</span>
                    </th>
                );
            }
            const sortKey = column.sortKey || column.key;
            const isSortCol = SortUtil.sortObjUsesKey(self.state.sort, sortKey);
            const sortIconElem =  isSortCol ? <TableHeaderSortIcon className="margin-left-sm" asc={SortUtil.sortObjIsAsc(self.state.sort)} /> : null;
            const sortOrder = isSortCol ? SortUtil.sortObjIsAsc(self.state.sort) ? "ascending" : "descending" : null;

            return (
                <th
                    key={column.key}
                    style={getColumnStyle(column)}
                    title={column.displayName}
                    role="columnheader"
                    scope="col"
                    aria-sort={sortOrder}
                >
                    <button
                        className={isSortCol ? "column-header-button link header-link link-selected" : "column-header-button link header-link"}
                        tabIndex={0}
                        onClick={self.sort.bind(self, column)}
                    >
                        {column.displayName}
                        {sortIconElem}
                    </button>
                </th>
            )
        }

        let filterChanged = true; // TODO
        let unfilteredPages = 1;
        if (this.props.rowsPerPage) {
            unfilteredPages = Math.ceil(this.props.rows.length / this.props.rowsPerPage);
        }
        
        let defaultPadding = this.props.padClassName || "pad-cells-md";

        return (
            <div className={this.props.className}>
                { !!this.props.title ?
                    <h2 className="view-title">{this.props.title}</h2>
                    :
                    null
                }
                {pageControls}
                { !this.props.hideSearch ?
                    <div className="search-components">
                        <input className="search"
                        style={{marginLeft:'5px', marginRight:'5px'}}
                        onChange={this.onFilterChange}
                        onKeyDown={this.onFilterKeyDown}
                        placeholder="Search"
                        ref="search_input" />
                        {unfilteredPages > 1 ?
                            <button
                                className="bare-text-button glyph"
                                disabled={!filterChanged}
                                onClick={this.onApplyFilterChange}
                            >
                                <i className={"fa fa-search" + (filterChanged ? "":" disabled")} aria-hidden />
                                <span className="sr-only">Filter</span>
                            </button>
                        : null }
                    </div>
                    :
                    null
                }

                { this.props.showTotal ?
                    <span className="fr">Total: {filtered_length}</span>
                    :
                    null
                }

                <div className="float-clear"/>

                <div className={`table-container ${this.props.classNameTableContainer}`}>
                    <table
                        className={`sortableTable2 ${defaultPadding} ${this.props.classNameTable}`}
                        aria-labelledby={this.props.label || null}
                    >
                        <thead className="table-header border-bottom">
                            {(this.props.topGroupHeader.length) > 0 &&
                                <tr className="top-group-header" role="row">
                                    {this.props.topGroupHeader.map((column, i) => (
                                        <th
                                            key={column.displayName || i}
                                            colSpan={column.colSpan}
                                            style={getColumnStyle(column)}
                                            title={column.displayName}
                                            role="columnheader"
                                            scope="col"
                                        >
                                            {column.displayName}
                                        </th>
                                    ))}
                                </tr>
                            }
                            <tr role="row">
                                {this.props.columns.map(column => (
                                    renderHeaderCol(column)
                                ))}
                            </tr>
                        </thead>
                            {rows.map((row) => {
                                const formattedRow = this.rowFormatter(row);
                                const uniqueRowKey = row[this.props.uniqueRowKey];
                                const extraRowContent = !!this.props.rowExtraElements ? this.props.rowExtraElements(uniqueRowKey) : null;
                                let optionalAttrs = {};
                                if (!!this.props.rowClassName) {
                                    const rowClassName = this.props.rowClassName(uniqueRowKey);
                                    if (rowClassName) optionalAttrs.className = rowClassName;
                                }

                                return (
                                    <tbody key={uniqueRowKey} {...optionalAttrs}>
                                        <tr
                                            role="row"
                                            className={self.rowClassNameGetter(row)}
                                            onMouseEnter={self.onMouseEnterRow}
                                            onMouseLeave={self.onMouseLeaveRow}
                                            onClick={self.onRowClick.bind(this, uniqueRowKey)}
                                        >
                                            {self.props.columns.map((column) => {
                                                const rowData = formattedRow[column.key] || {};
                                                const optionalAttrs = {};
                                                if (!!column.generateId) {
                                                    optionalAttrs.id = `${uniqueRowKey}-${column.key}`;
                                                }

                                                return (
                                                    <td
                                                        key={column.key}
                                                        style={getColumnStyle(column)}
                                                        title={rowData.text}
                                                        aria-label={rowData.text}
                                                        {...optionalAttrs}
                                                    >
                                                        {rowData.display_elem}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {(!!extraRowContent) ?
                                            <tr>
                                                <td id={`${uniqueRowKey}-extra`} colSpan={this.props.columns.length}>
                                                    {extraRowContent}
                                                </td>
                                            </tr>
                                            :
                                            !!self.props.alwaysRenderExtraRowContent ?
                                                <tr>
                                                    <td
                                                        id={`${uniqueRowKey}-extra`}
                                                        className="collapse"
                                                        colSpan={this.props.columns.length}
                                                    />
                                                </tr>
                                                :
                                                null
                                        }
                                    </tbody>
                                );
                            })}
                    </table>
                </div>
            </div>
        )
    }
}

SortableTable2.propTypes = propTypes;
SortableTable2.defaultProps = defaultProps;

export default SortableTable2;